/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { Plugin } from './plugin'
import { SchoolPluginTag } from './school-plugin-tag'
import { SortSchoolPluginTag } from './sort-school-plugin-tag'
import { SchoolPlugin } from './school-plugin'
import { EnabledPlugin } from './enabled-plugin'
import { SortSchoolPlugin } from './sort-school-plugin'
import { SourceEnum } from './source-enum'
import { SchoolPluginReq } from './school-plugin-req'
import { QueryPluginsQuery } from './query-plugins-query'
import { QuerySchoolPluginsQuery } from './query-school-plugins-query'
import { QueryEnabledSchoolPluginsQuery } from './query-enabled-school-plugins-query'
import { MailLoginUrlForNoticeQuery } from './mail-login-url-for-notice-query'
import { BatchSortSchoolPluginQuery } from './batch-sort-school-plugin-query'

const buildConfig = createConfigBuilder('growp')

export const pluginApi = {
  queryPluginsConfig(
    queryPluginsQuery?: QueryPluginsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/plugin/plugins', {}, queryPluginsQuery)
  },

  /**
   * @summary 【后台】查询系统应用
   * @param [queryPluginsQuery]
   * @param [queryPluginsQuery.labelLike] 根据应用名称模糊搜索
   * @param [queryPluginsQuery.source] 根据应用来源搜索
   * @param [queryPluginsQuery.paginated] 是否分页，默认 1
   * @param [queryPluginsQuery.page] 显示的数据页，默认 1
   * @param [queryPluginsQuery.perPage] 每页显示数量，默认 20
   * @param [queryPluginsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Plugin[]>
   */
  queryPlugins(
    queryPluginsQuery?: QueryPluginsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Plugin[]> {
    return axios(pluginApi.queryPluginsConfig(queryPluginsQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new Plugin(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryPlugins(
    query?: QueryPluginsQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Plugin[]> {
    return useQueryApi<Plugin[]>(
      pluginApi.queryPlugins,
      'Plugin.queryPlugins',
      queryOptions,
      query
    )
  },

  querySchoolPluginTagsConfig(
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/plugin/school-plugin-tags', {})
  },

  /**
   * @summary 【后/前台】查询学校应用
   *  - 不支持分页
   * @param [options]
   * @return AxiosResponsePromise<SchoolPluginTag[]>
   */
  querySchoolPluginTags(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SchoolPluginTag[]> {
    return axios(pluginApi.querySchoolPluginTagsConfig(options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data
          ? _data.map((d: any) => new SchoolPluginTag(d))
          : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQuerySchoolPluginTags(
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<SchoolPluginTag[]> {
    return useQueryApi<SchoolPluginTag[]>(
      pluginApi.querySchoolPluginTags,
      'Plugin.querySchoolPluginTags',
      queryOptions
    )
  },

  batchSortSchoolPluginTagConfig(
    sortSchoolPluginTag: SortSchoolPluginTag[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/plugin/school-plugin-tags/sort/$batch',
      {},
      null,
      sortSchoolPluginTag,
      {
        format: 'json',
        isArray: true,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】批量排序应用分类
   * @param sortSchoolPluginTag
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  batchSortSchoolPluginTag(
    sortSchoolPluginTag: SortSchoolPluginTag[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      pluginApi.batchSortSchoolPluginTagConfig(sortSchoolPluginTag, options)
    ) as any
  },

  useBatchSortSchoolPluginTag(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: SortSchoolPluginTag[]) =>
        pluginApi.batchSortSchoolPluginTag(payload),
      mutationOptions
    )
  },

  deleteSchoolPluginTagConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/plugin/school-plugin-tags/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】删除学校应用分类
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteSchoolPluginTag(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(pluginApi.deleteSchoolPluginTagConfig(id, options)) as any
  },

  useDeleteSchoolPluginTag(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => pluginApi.deleteSchoolPluginTag(id),
      mutationOptions
    )
  },

  querySchoolPluginsConfig(
    querySchoolPluginsQuery?: QuerySchoolPluginsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/plugin/school-plugins',
      {},
      querySchoolPluginsQuery
    )
  },

  /**
   * @summary 【后台】查询学校应用
   * @param [querySchoolPluginsQuery]
   * @param [querySchoolPluginsQuery.labelLike] 根据应用名称模糊搜索
   * @param [querySchoolPluginsQuery.statusIn] 根据应用状态筛选
   * @param [querySchoolPluginsQuery.tagId] 根据应用分类筛选
   *  - 全部：不传
   *  - 未分类：0
   *  - 其它分类：传 term id 值
   * @param [querySchoolPluginsQuery.expand]
   * @param [querySchoolPluginsQuery.pluginIdIn] 根据应用来源筛选
   * @param [querySchoolPluginsQuery.paginated] 是否分页，默认 1
   * @param [querySchoolPluginsQuery.page] 显示的数据页，默认 1
   * @param [querySchoolPluginsQuery.perPage] 每页显示数量，默认 20
   * @param [querySchoolPluginsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<SchoolPlugin[]>
   */
  querySchoolPlugins<TQuery extends QuerySchoolPluginsQuery>(
    querySchoolPluginsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<SchoolPlugin, TQuery['expand']>[]> {
    return axios(
      pluginApi.querySchoolPluginsConfig(querySchoolPluginsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new SchoolPlugin(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQuerySchoolPlugins<TQuery extends QuerySchoolPluginsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<SchoolPlugin, TQuery['expand']>[]> {
    return useQueryApi<Expand<SchoolPlugin, TQuery['expand']>[]>(
      pluginApi.querySchoolPlugins,
      'Plugin.querySchoolPlugins',
      queryOptions,
      query
    )
  },

  queryEnabledSchoolPluginsConfig(
    queryEnabledSchoolPluginsQuery?: QueryEnabledSchoolPluginsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/plugin/school-plugins/enabled',
      {},
      queryEnabledSchoolPluginsQuery
    )
  },

  /**
   * @summary 【前台】查询学校应用
   * @param [queryEnabledSchoolPluginsQuery]
   * @param [queryEnabledSchoolPluginsQuery.labelLike] 根据应用名称模糊搜索
   * @param [queryEnabledSchoolPluginsQuery.tagId] 根据应用分类筛选
   *  - 全部：不传
   *  - 未分类：0
   *  - 其它分类：传 term id 值
   * @param [queryEnabledSchoolPluginsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<EnabledPlugin>
   */
  queryEnabledSchoolPlugins<TQuery extends QueryEnabledSchoolPluginsQuery>(
    queryEnabledSchoolPluginsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<EnabledPlugin, TQuery['expand']>> {
    return axios(
      pluginApi.queryEnabledSchoolPluginsConfig(
        queryEnabledSchoolPluginsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new EnabledPlugin(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryEnabledSchoolPlugins<TQuery extends QueryEnabledSchoolPluginsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<EnabledPlugin, TQuery['expand']>> {
    return useQueryApi<Expand<EnabledPlugin, TQuery['expand']>>(
      pluginApi.queryEnabledSchoolPlugins,
      'Plugin.queryEnabledSchoolPlugins',
      queryOptions,
      query
    )
  },

  mailLoginUrlForNoticeConfig(
    mailLoginUrlForNoticeQuery?: MailLoginUrlForNoticeQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/plugin/school-plugins/mail-notice-login-url',
      {},
      mailLoginUrlForNoticeQuery
    )
  },

  /**
   * @summary 为查看通知获取网易企业邮箱单点登录地址
   * @param [mailLoginUrlForNoticeQuery]
   * @param [mailLoginUrlForNoticeQuery.mid]
   * @param [options]
   * @return AxiosResponsePromise<string>
   */
  mailLoginUrlForNotice(
    mailLoginUrlForNoticeQuery?: MailLoginUrlForNoticeQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<string> {
    return axios(
      pluginApi.mailLoginUrlForNoticeConfig(mailLoginUrlForNoticeQuery, options)
    ) as any
  },

  useMailLoginUrlForNotice(
    query?: MailLoginUrlForNoticeQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<string> {
    return useQueryApi<string>(
      pluginApi.mailLoginUrlForNotice,
      'Plugin.mailLoginUrlForNotice',
      queryOptions,
      query
    )
  },

  batchSortSchoolPluginConfig(
    sortSchoolPlugin: SortSchoolPlugin[],
    batchSortSchoolPluginQuery?: BatchSortSchoolPluginQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/plugin/school-plugins/sort/$batch',
      {},
      batchSortSchoolPluginQuery,
      sortSchoolPlugin,
      {
        format: 'json',
        isArray: true,
        defaults: {
          groupWeight: (v: any) => v || 0,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】批量排序/分组排序学校应用
   * @param sortSchoolPlugin
   * @param [batchSortSchoolPluginQuery]
   * @param [batchSortSchoolPluginQuery.isGroupSort] 是否是小组内排序，默认否
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  batchSortSchoolPlugin(
    sortSchoolPlugin: SortSchoolPlugin[],
    batchSortSchoolPluginQuery?: BatchSortSchoolPluginQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      pluginApi.batchSortSchoolPluginConfig(
        sortSchoolPlugin,
        batchSortSchoolPluginQuery,
        options
      )
    ) as any
  },

  useBatchSortSchoolPlugin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: SortSchoolPlugin[]
        query?: BatchSortSchoolPluginQuery
      }) => pluginApi.batchSortSchoolPlugin(payload, query),
      mutationOptions
    )
  },

  createSchoolPluginConfig(
    source: SourceEnum,
    schoolPluginReq: SchoolPluginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/plugin/school-plugins/source/{source}',
      {
        source,
      },
      null,
      schoolPluginReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          icon: (v: any) => v || '',
          description: (v: any) => v || '',
          tagId: (v: any) => v || 0,
          entryUrls: (v: any) => v || null,
          scopes: (v: any) => v || null,
          weight: (v: any) => v || 0,
          groupWeight: (v: any) => v || 0,
          status: (v: any) => v || null,
          sourcePluginId: (v: any) => v || 0,
          config: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】创建学校应用
   * @param source
   * @param schoolPluginReq
   * @param [options]
   * @return AxiosResponsePromise<SchoolPlugin>
   */
  createSchoolPlugin(
    source: SourceEnum,
    schoolPluginReq: SchoolPluginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SchoolPlugin> {
    return axios(
      pluginApi.createSchoolPluginConfig(source, schoolPluginReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new SchoolPlugin(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateSchoolPlugin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ source, payload }: { source: SourceEnum; payload: SchoolPluginReq }) =>
        pluginApi.createSchoolPlugin(source, payload),
      mutationOptions
    )
  },

  deleteSchoolPluginConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/plugin/school-plugins/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】删除学校应用
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteSchoolPlugin(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(pluginApi.deleteSchoolPluginConfig(id, options)) as any
  },

  useDeleteSchoolPlugin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => pluginApi.deleteSchoolPlugin(id),
      mutationOptions
    )
  },

  updateSchoolPluginConfig(
    id: number,
    schoolPluginReq: SchoolPluginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/plugin/school-plugins/{id}',
      {
        id,
      },
      null,
      schoolPluginReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          icon: (v: any) => v || '',
          description: (v: any) => v || '',
          tagId: (v: any) => v || 0,
          entryUrls: (v: any) => v || null,
          scopes: (v: any) => v || null,
          weight: (v: any) => v || 0,
          groupWeight: (v: any) => v || 0,
          status: (v: any) => v || null,
          sourcePluginId: (v: any) => v || 0,
          config: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】更新学校应用
   * @param id
   * @param schoolPluginReq
   * @param [options]
   * @return AxiosResponsePromise<SchoolPlugin>
   */
  updateSchoolPlugin(
    id: number,
    schoolPluginReq: SchoolPluginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SchoolPlugin> {
    return axios(
      pluginApi.updateSchoolPluginConfig(id, schoolPluginReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new SchoolPlugin(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateSchoolPlugin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: SchoolPluginReq }) =>
        pluginApi.updateSchoolPlugin(id, payload),
      mutationOptions
    )
  },

  enabledSchoolPluginConfig(
    enabled: boolean,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'put', '/plugin/school-plugins/{id}/enabled', {
      enabled,
      id,
    })
  },

  /**
   * @summary 【后台】启用/取消启用应用
   * @param enabled 是否启用-默认是
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  enabledSchoolPlugin(
    enabled: boolean,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      pluginApi.enabledSchoolPluginConfig(enabled, id, options)
    ) as any
  },

  useEnabledSchoolPlugin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ enabled, id }: { enabled: boolean; id: number }) =>
        pluginApi.enabledSchoolPlugin(enabled, id),
      mutationOptions
    )
  },

  mailLoginUrlConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/plugin/school-plugins/{id}/mail-login-url',
      {
        id,
      }
    )
  },

  /**
   * @summary 获取网易企业邮箱单点登录地址
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<string>
   */
  mailLoginUrl(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<string> {
    return axios(pluginApi.mailLoginUrlConfig(id, options)) as any
  },

  useMailLoginUrl(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => pluginApi.mailLoginUrl(id),
      mutationOptions
    )
  },

  mailSubscribeCallbackConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/plugin/school-plugins/{id}/mail-subscribe/callback',
      {
        id,
      }
    )
  },

  /**
   * @summary 网易企业邮箱订阅回调
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  mailSubscribeCallback(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(pluginApi.mailSubscribeCallbackConfig(id, options)) as any
  },

  useMailSubscribeCallback(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => pluginApi.mailSubscribeCallback(id),
      mutationOptions
    )
  },

  pinnedSchoolPluginConfig(
    pinned: boolean,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'put', '/plugin/school-plugins/{id}/pinned', {
      pinned,
      id,
    })
  },

  /**
   * @summary 【前台】置顶/取消置顶应用
   * @param pinned 是否置顶-默认是
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  pinnedSchoolPlugin(
    pinned: boolean,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(pluginApi.pinnedSchoolPluginConfig(pinned, id, options)) as any
  },

  usePinnedSchoolPlugin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ pinned, id }: { pinned: boolean; id: number }) =>
        pluginApi.pinnedSchoolPlugin(pinned, id),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(pluginApi).forEach((api) => {
  // @ts-ignore
  pluginApi[api]._name_ = `Plugin.${api}`
})
